"use client";

import { createContext, useContext } from "react";
import type { SiteSettings } from "@/services/settingsShared";

/**
 * Carries the settings the ROOT LAYOUT already fetched on the server down to
 * every client component, as the starting value for useSiteSettings.
 *
 * That seed is the whole point: consumers used to start at null and fill in
 * from an effect, so the server rendered the bundled logo, the browser painted
 * it, and the region's logo replaced it a tick later — a visible swap on every
 * cold load even though the server knew the right answer all along.
 *
 * It holds no state of its own. Keeping the live value in the client cache
 * (services/settingsClient) rather than here is what lets a region switch
 * update every consumer without this provider re-rendering — which matters
 * because proxy.ts rewrites /eg/en to /en, so both regions are the same Next
 * route and this provider survives the navigation with a stale seed.
 */
const SiteSettingsSeedContext = createContext<SiteSettings | null>(null);

export function SiteSettingsProvider({
  initialSettings,
  children,
}: {
  initialSettings: SiteSettings | null;
  children: React.ReactNode;
}) {
  return (
    <SiteSettingsSeedContext.Provider value={initialSettings}>
      {children}
    </SiteSettingsSeedContext.Provider>
  );
}

export function useSiteSettingsSeed(): SiteSettings | null {
  return useContext(SiteSettingsSeedContext);
}
